import * as yup from 'yup';

// Evaluation form data interface
export interface CreateEvaluationFormData {
  teacher_id: string;
  rubric_template_id: string;
  evaluation_period: string;
  evaluation_type: 'formal' | 'informal' | 'summative';
  notes?: string | null;
  status?: 'draft' | 'pending' | 'in_progress' | 'scored' | 'completed' | 'approved' | 'archived';
  // Lesson details fields
  subject: string;
  grade_level: string;
  lesson_title: string;
  duration_minutes: string;
  student_count: string;
  special_considerations?: string;
  technology_used?: string;
  assessment_methods?: string;
}

export interface UpdateEvaluationFormData {
  teacher_id?: string;
  rubric_template_id?: string;
  evaluation_period?: string;
  evaluation_type?: 'formal' | 'informal' | 'summative';
  notes?: string | null;
  status?: 'draft' | 'pending' | 'in_progress' | 'scored' | 'completed' | 'approved' | 'archived';
  // Lesson details fields
  subject?: string;
  grade_level?: string;
  lesson_title?: string;
  duration_minutes?: string;
  student_count?: string;
  special_considerations?: string;
  technology_used?: string;
  assessment_methods?: string;
}

// Yup validation schemas
export const createEvaluationSchema = yup.object({
  teacher_id: yup
    .string()
    .required('Teacher is required')
    .min(1, 'Teacher is required'),
  
  rubric_template_id: yup
    .string()
    .required('Evaluation framework is required')
    .min(1, 'Evaluation framework cannot be empty'),
  
  evaluation_period: yup
    .string()
    .required('Academic year is required')
    .min(1, 'Academic year is required'),
  
  evaluation_type: yup
    .string()
    .required('Evaluation type is required')
    .oneOf(['formal', 'informal', 'summative'], 'Invalid evaluation type selected'),
  
  notes: yup
    .string()
    .nullable()
    .optional()
    .max(1000, 'Notes must be no more than 1000 characters'),

  status: yup
    .string()
    .optional()
    .oneOf(['draft', 'pending', 'in_progress', 'scored', 'completed', 'approved', 'archived'], 'Invalid status'),

  // Lesson details validation
  subject: yup
    .string()
    .required('Subject is required')
    .min(1, 'Subject cannot be empty'),
  
  grade_level: yup
    .string()
    .required('Grade level is required')
    .min(1, 'Grade level cannot be empty'),
  
  lesson_title: yup
    .string()
    .required('Lesson title is required')
    .min(1, 'Lesson title cannot be empty'),
  
  duration_minutes: yup
    .string()
    .required('Duration is required')
    .test('is-number', 'Duration must be a valid number', (value) => {
      if (!value) return false;
      const num = parseInt(value);
      return !isNaN(num) && num > 0 && num <= 180;
    }),
  
  student_count: yup
    .string()
    .required('Student count is required')
    .test('is-number', 'Student count must be a valid number', (value) => {
      if (!value) return false;
      const num = parseInt(value);
      return !isNaN(num) && num > 0 && num <= 50;
    }),
  
  special_considerations: yup
    .string()
    .optional()
    .max(500, 'Special considerations must be no more than 500 characters'),
  
  technology_used: yup
    .string()
    .optional()
    .max(200, 'Technology used must be no more than 200 characters'),
  
  assessment_methods: yup
    .string()
    .optional()
    .max(200, 'Assessment methods must be no more than 200 characters'),
}).required();

export const updateEvaluationSchema = yup.object({
  teacher_id: yup
    .string()
    .optional()
    .min(1, 'Teacher ID cannot be empty'),
  
  rubric_template_id: yup
    .string()
    .optional()
    .min(1, 'Evaluation framework is required'),
  
  evaluation_period: yup
    .string()
    .optional()
    .min(1, 'Evaluation period cannot be empty'),
  
  evaluation_type: yup
    .string()
    .optional()
    .oneOf(['formal', 'informal', 'summative'], 'Invalid evaluation type selected'),
  
  notes: yup
    .string()
    .nullable()
    .optional()
    .max(1000, 'Notes must be no more than 1000 characters'),
});

// Rubric template options
export const rubricTemplates = [
  { id: 'danielson_2022', name: 'Danielson Framework for Teaching (2022)' },
  { id: 'marzano_v3', name: 'Marzano Teacher Evaluation (v3)' },
  { id: 'cel_5d_plus', name: 'CEL 5D+ Teaching and Learning Framework' },
  { id: 'otes_2_0', name: 'Ohio Teacher Evaluation System (OTES 2.0)' },
];

// Evaluation type options
export const evaluationTypes = [
  { id: 'formal', name: 'Formal Evaluation' },
  { id: 'informal', name: 'Informal Observation' },
  { id: 'summative', name: 'Summative Assessment' },
];

// Default form values
export const defaultCreateEvaluationValues: CreateEvaluationFormData = {
  teacher_id: '',
  rubric_template_id: '',
  evaluation_period: '',
  evaluation_type: 'formal',
  notes: null,
  status: 'draft',
  // Lesson details defaults
  subject: '',
  grade_level: '',
  lesson_title: '',
  duration_minutes: '',
  student_count: '',
  special_considerations: '',
  technology_used: '',
  assessment_methods: '',
};
